import React, { useState, useEffect } from 'react';
import { getClinicImages, uploadClinicImage, deleteClinicImage } from '../../services/api';
import Button from '../common/Button';
import ConfirmDialog from '../common/ConfirmDialog';
import ImageCarousel from '../common/ImageCarousel';

const IMAGE_TYPES = [
    'THUMBNAIL',
    'INTERIOR',
    'EXTERIOR',
    'CLINIC',
    'OT',
    'STAFF',
    'DOCTOR',
    'WAITING_AREA',
    'RECEPTION',
    'CONSULTATION_ROOM'
];

// Responsive hook
function useIsMobile() {
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 640);
    React.useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
}

const ClinicImagesTab = ({ clinicId }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState({});

    // Confirmation dialog state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [imageTypeToDelete, setImageTypeToDelete] = useState(null);

    // Upload form state
    const [uploadData, setUploadData] = useState({
        imageType: '',
        description: '',
        image: null
    });
    const [imagePreview, setImagePreview] = useState('');

    const isMobile = useIsMobile();

    useEffect(() => {
        fetchClinicImages();
    }, [clinicId]);

    const fetchClinicImages = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getClinicImages(clinicId);
            if (response.success) {
                setImages(response.data || []);
            } else {
                setError(response.message || 'Failed to fetch clinic images');
            }
        } catch (err) {
            setError('Failed to fetch clinic images. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadData(prev => ({ ...prev, image: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        
        if (!uploadData.imageType || !uploadData.description || !uploadData.image) {
            setError('Please fill all fields and select an image');
            return;
        }

        setUploadLoading(true);
        setError(null);

        try {
            const response = await uploadClinicImage(clinicId, uploadData);
            if (response.success) {
                setShowUploadModal(false);
                setUploadData({ imageType: '', description: '', image: null });
                setImagePreview('');
                fetchClinicImages(); // Refresh the images list
            } else {
                setError(response.message || 'Failed to upload image');
            }
        } catch (err) {
            setError('Failed to upload image. Please try again.');
        } finally {
            setUploadLoading(false);
        }
    };

    const handleDeleteClick = (imageUrl, imageType) => {
        setImageToDelete(imageUrl);
        setImageTypeToDelete(imageType);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!imageToDelete || !imageTypeToDelete) return;

        setDeleteLoading(prev => ({ ...prev, [imageToDelete]: true }));
        setError(null);

        try {
            const response = await deleteClinicImage(clinicId, imageToDelete, imageTypeToDelete);
            if (response.success) {
                fetchClinicImages(); // Refresh the images list
            } else {
                setError(response.message || 'Failed to delete image');
            }
        } catch (err) {
            setError('Failed to delete image. Please try again.');
        } finally {
            setDeleteLoading(prev => ({ ...prev, [imageToDelete]: false }));
            setShowDeleteConfirm(false);
            setImageToDelete(null);
            setImageTypeToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
        setImageToDelete(null);
        setImageTypeToDelete(null);
    };

    const groupImagesByType = () => {
        const grouped = {};
        images.forEach(image => {
            if (!grouped[image.imageType]) {
                grouped[image.imageType] = [];
            }
            grouped[image.imageType].push(image);
        });
        return grouped;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading clinic images...</p>
                </div>
            </div>
        );
    }

    const groupedImages = groupImagesByType();

    return (
        <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Clinic Images</h3>
                <Button
                    type="button"
                    variant="primary"
                    onClick={() => setShowUploadModal(true)}
                >
                    Add New Image
                </Button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                </div>
            )}

            {/* Images by Type */}
            {Object.keys(groupedImages).length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <p className="text-gray-500">No images uploaded yet.</p>
                    <p className="text-gray-400 text-sm">Click "Add New Image" to get started.</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {Object.entries(groupedImages).map(([imageType, typeImages]) => (
                        <div key={imageType} className="bg-white rounded-lg shadow-sm border">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h4 className="text-lg font-medium text-gray-900 capitalize">
                                    {imageType.replace(/_/g, ' ').toLowerCase()}
                                </h4>
                                <p className="text-sm text-gray-500">
                                    {typeImages.length} image{typeImages.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                            <div className="p-4 sm:p-6">
                                {isMobile ? (
                                    <ImageCarousel
                                        images={typeImages.map(img => ({ url: img.imageUrl, alt: img.description }))}
                                        onImageClick={(img, idx) => window.open(img.url, '_blank')}
                                        showNavigation={true}
                                        autoSlide={false}
                                    />
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                                        {typeImages.map((image) => (
                                            <div key={image.id} className="bg-gray-50 rounded-lg overflow-hidden">
                                                <div className="aspect-w-16 aspect-h-9">
                                                    <img
                                                        src={image.imageUrl}
                                                        alt={image.description}
                                                        className="w-full h-48 object-cover"
                                                        onError={(e) => {
                                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04MCAxMDBDODAgODkuNTQ0NyA4OC4wMDAxIDgxIDEwMCA4MUMxMTEuOTU2IDgxIDEyMCA4OS41NDQ3IDEyMCAxMDBDMTIwIDExMC40NTUgMTExLjk1NiAxMTkgMTAwIDExOUM4OC4wMDAxIDExOSA4MCAxMTAuNDU1IDgwIDEwMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTEwMCAxMzBDMTEwLjQ1NSAxMzAgMTE5IDEyMS40NTUgMTE5IDExMEMxMTkgOTguNTQ0NyAxMTAuNDU1IDkwIDEwMCA5MEM4OS4wMDAxIDkwIDgxIDk4LjU0NDcgODEgMTEwQzgxIDEyMS40NTUgODkuMDAwMSAxMzAgMTAwIDEzMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                                                        }}
                                                    />
                                                </div>
                                                <div className="p-3 sm:p-4">
                                                    <p className="text-sm font-medium text-gray-900 mb-1">
                                                        {image.description}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mb-3">
                                                        Uploaded: {formatDate(image.uploadedAt)}
                                                    </p>
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => handleDeleteClick(image.imageUrl, image.imageType)}
                                                        disabled={deleteLoading[image.imageUrl]}
                                                        className="w-full"
                                                    >
                                                        {deleteLoading[image.imageUrl] ? 'Deleting...' : 'Delete'}
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-md mx-auto">
                        <div className="p-4 sm:p-6">
                            <div className="flex justify-between items-center mb-4 sm:mb-6">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Upload New Image
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowUploadModal(false);
                                        setUploadData({ imageType: '', description: '', image: null });
                                        setImagePreview('');
                                        setError(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>

                            <form onSubmit={handleUpload} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Image Type *
                                    </label>
                                    <select
                                        value={uploadData.imageType}
                                        onChange={(e) => setUploadData(prev => ({ ...prev, imageType: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        required
                                    >
                                        <option value="">Select image type</option>
                                        {IMAGE_TYPES.map(type => (
                                            <option key={type} value={type}>
                                                {type.replace(/_/g, ' ')}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description *
                                    </label>
                                    <input
                                        type="text"
                                        value={uploadData.description}
                                        onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        placeholder="Enter image description"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Image *
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageSelect}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>

                                {imagePreview && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Preview
                                        </label>
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-full h-32 object-cover rounded-md border"
                                        />
                                    </div>
                                )}

                                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => {
                                            setShowUploadModal(false);
                                            setUploadData({ imageType: '', description: '', image: null });
                                            setImagePreview('');
                                            setError(null);
                                        }}
                                        disabled={uploadLoading}
                                        className="w-full sm:w-auto"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={uploadLoading}
                                        className="w-full sm:w-auto"
                                    >
                                        {uploadLoading ? 'Uploading...' : 'Upload'}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Delete Image"
                message="Are you sure you want to delete this image? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                confirmVariant="danger"
                cancelVariant="secondary"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={deleteLoading[imageToDelete]}
            />
        </div>
    );
};

export default ClinicImagesTab; 